import { useState } from 'react'
import { AuthCard } from './AuthCard'
import { AuthInput } from './AuthInput'
import { AuthButton } from './AuthButton'
import { LogIn, Shield, UserPlus, Lock } from 'lucide-react'
import { AuthState, SignInResult } from '@/hooks/useCustomAuth'
import { signInWithRedirect } from 'aws-amplify/auth'
import { useTenant } from '@/lib/tenant/tenant-provider'

interface SignInFormData {
  username: string
  email: string
  phoneNumber: string
  password: string
}

interface SignInFormProps {
  onSuccess?: () => void
  authState: any // CustomAuthState
  handleSignIn: (username: string, password: string) => Promise<SignInResult>
  changeState: (newState: AuthState, tempEmail?: string, fromLogin?: boolean) => void
  userPoolConfig?: any // UserPoolConfig
}

export function SignInForm({ onSuccess, authState, handleSignIn, changeState, userPoolConfig }: SignInFormProps) {
  const { tenant } = useTenant()
  const [formData, setFormData] = useState<SignInFormData>({
    username: '',
    email: '',
    phoneNumber: '',
    password: ''
  })

  // Check if email/password login is enabled
  // Enable for E2E testing - can also be controlled via tenant config
  const isEmailPasswordEnabled = tenant?.amplifyConfig?.Auth?.Cognito?.loginWith?.email ?? true

  // Check if OAuth is configured
  const oauthConfig = tenant?.amplifyConfig?.Auth?.Cognito?.loginWith?.oauth
  const hasOAuthProviders = true // Force showing OAuth buttons
  // const hasOAuthProviders = oauthConfig && oauthConfig.providers && oauthConfig.providers.length > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Determine which field to use as username based on configuration
    let username = formData.email
    if (userPoolConfig?.signInIdentifiers) {
      const { signInIdentifiers } = userPoolConfig

      if (!signInIdentifiers.username && signInIdentifiers.email) {
        username = formData.email
      } else if (!signInIdentifiers.username && signInIdentifiers.phone) {
        username = formData.phoneNumber
      } else if (signInIdentifiers.email && formData.email) {
        username = formData.email
      } else if (signInIdentifiers.phone && formData.phoneNumber) {
        username = formData.phoneNumber
      }
    }

    try {
      const result = await handleSignIn(username, formData.password)

      // Only call onSuccess if fully authenticated (not if needs new password or confirmation)
      if (result?.success && !result?.needsNewPassword && !result?.needsConfirmation) {
        onSuccess?.()
      }
    } catch (error) {
      console.error('Sign in error:', error)
    }
  }

  const updateFormData = (field: keyof SignInFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleAzureSAMLSignIn = async () => {
    try {
      await signInWithRedirect({
        provider: {
          custom: 'azure'
        }
      })
    } catch (error: any) {
      console.error('Azure SAML sign in error:', error)
      const errorMessage = error?.message?.includes('oauth param not configured')
        ? 'Azure SSO precisa ser configurado no AWS Cognito primeiro.'
        : 'Erro ao conectar com Azure. Tente novamente.'

      console.error('🚨 Azure SAML Configuration Error:', errorMessage)
    }
  }

  const handleDevSquadSignIn = async () => {
    try {
      await signInWithRedirect({
        provider: {
          custom: 'DevSquad'
        }
      })
    } catch (error: any) {
      console.error('DevSquad sign in error:', error)
      const errorMessage = error?.message?.includes('oauth param not configured')
        ? 'DevSquad SSO precisa ser configurado no AWS Cognito primeiro.'
        : 'Erro ao conectar com DevSquad. Tente novamente.'

      console.error('🚨 DevSquad Configuration Error:', errorMessage)
    }
  }

  const handleWrikeSignIn = async () => {
    try {
      await signInWithRedirect({
        provider: {
          custom: 'Wrike'
        }
      })
    } catch (error: any) {
      console.error('Wrike sign in error:', error)
      const errorMessage = error?.message?.includes('oauth param not configured')
        ? 'Wrike SSO não está configurado no AWS Cognito.'
        : 'Erro ao conectar com Wrike. Tente novamente.'

      console.error('🚨 Wrike Configuration Error:', errorMessage)
    }
  }

  // Helper function to check if form is valid
  const isFormValid = () => {
    if (!formData.password) return false

    if (userPoolConfig?.signInIdentifiers) {
      const { signInIdentifiers } = userPoolConfig

      // If only email is allowed
      if (!signInIdentifiers.username && signInIdentifiers.email && !signInIdentifiers.phone) {
        return !!formData.email
      }

      // If only phone is allowed
      if (!signInIdentifiers.username && !signInIdentifiers.email && signInIdentifiers.phone) {
        return !!formData.phoneNumber
      }

      // If username is allowed (with or without aliases)
      if (signInIdentifiers.username) {
        return !!formData.username || !!formData.email || !!formData.phoneNumber
      }

      // At least one field must be filled
      return !!formData.email || !!formData.phoneNumber
    }

    // Default: require email
    return !!formData.email
  }

  return (
    <AuthCard
      title="Bem-vindo de volta"
      subtitle={isEmailPasswordEnabled ? "Entre com suas credenciais para acessar o sistema" : "Entre com sua conta Wrike"}
      icon={<Shield className="h-8 w-8 text-brand-dark" />}
    >
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="signin-form">
        {/* Email/Password Form - Only show if enabled */}
        {isEmailPasswordEnabled && (
          <>
            {/* Render username field if supported */}
            {userPoolConfig?.signInIdentifiers?.username && (
          <AuthInput
            label="Nome de usuário"
            type="text"
            value={formData.username}
            onChange={(value) => updateFormData('username', value)}
            placeholder="Digite seu nome de usuário"
            required={!userPoolConfig.signInIdentifiers.email && !userPoolConfig.signInIdentifiers.phone}
            error={authState.error?.code === 'UserNotFoundException' ? authState.error.message : undefined}
            data-testid="signin-username-input"
          />
        )}

        {/* Render email field if supported */}
        {userPoolConfig?.signInIdentifiers?.email && (
          <AuthInput
            label="E-mail"
            type="email"
            value={formData.email}
            onChange={(value) => updateFormData('email', value)}
            placeholder="seu.email@monumenta.com.br"
            required={!userPoolConfig.signInIdentifiers.username && !userPoolConfig.signInIdentifiers.phone}
            error={authState.error?.code === 'UserNotFoundException' ? authState.error.message : undefined}
            data-testid="signin-email-input"
          />
        )}

        {/* Render phone field if supported */}
        {userPoolConfig?.signInIdentifiers?.phone && (
          <AuthInput
            label="Telefone"
            type="tel"
            value={formData.phoneNumber}
            onChange={(value) => updateFormData('phoneNumber', value)}
            placeholder="+55 11 99999-9999"
            required={!userPoolConfig.signInIdentifiers.username && !userPoolConfig.signInIdentifiers.email}
            error={authState.error?.code === 'UserNotFoundException' ? authState.error.message : undefined}
            data-testid="signin-phone-input"
          />
        )}

        {/* Fallback: render email field if no user pool config */}
        {!userPoolConfig && (
          <AuthInput
            label="E-mail"
            type="email"
            value={formData.email}
            onChange={(value) => updateFormData('email', value)}
            placeholder="seu.email@monumenta.com.br"
            required
            error={authState.error?.code === 'UserNotFoundException' ? authState.error.message : undefined}
            data-testid="signin-email-input-fallback"
          />
        )}

        <AuthInput
          label="Senha"
          type="password"
          value={formData.password}
          onChange={(value) => updateFormData('password', value)}
          placeholder="Digite sua senha"
          required
          error={authState.error?.code === 'NotAuthorizedException' ? authState.error.message : undefined}
          data-testid="signin-password-input"
        />

        {authState.error && !['UserNotFoundException', 'NotAuthorizedException'].includes(authState.error.code || '') && (
          <div
            className="bg-error/20 border border-error text-error px-4 py-3 rounded-monumenta text-sm"
            data-testid="signin-error-message"
          >
            {authState.error.message}
          </div>
        )}

            <AuthButton
              type="submit"
              variant="primary"
              loading={authState.isLoading}
              disabled={!isFormValid()}
              className="w-full"
              icon={<LogIn className="h-5 w-5" />}
              data-testid="signin-submit-button"
            >
              Entrar
            </AuthButton>
          </>
        )}

        {/* Divider - Only show if both email/password AND OAuth are enabled */}
        {isEmailPasswordEnabled && hasOAuthProviders && (
          <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-brand-gray/20"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-brand-dark text-brand-gray font-body">ou</span>
          </div>
        </div>
        )}

        {/* SSO Buttons - Show if OAuth is configured */}
        {hasOAuthProviders && (
          <div className="space-y-3">
          {/* Azure SAML SSO Button */}
          {/* <AuthButton
            type="button"
            variant="secondary"
            onClick={handleAzureSAMLSignIn}
            className="w-full"
            icon={
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4z"/>
                <path d="M24 11.4H12.6V0H24v11.4z" fill="#00BCF2"/>
              </svg>
            }
            data-testid="signin-azure-sso-button"
          >
            Entrar com Monumenta
          </AuthButton> */}

          {/* DevSquad SSO Button */}
          {/* <AuthButton
            type="button"
            variant="secondary"
            onClick={handleDevSquadSignIn}
            className="w-full"
            icon={
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            }
            data-testid="signin-devsquad-sso-button"
          >
            Entrar com DevSquad
          </AuthButton> */}

          {/* Wrike SSO Button */}
          <AuthButton
            type="button"
            variant="secondary"
            onClick={handleWrikeSignIn}
            className="w-full"
            icon={
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2 3h20v2H2V3zm0 4h20v2H2V7zm0 4h20v2H2v-2zm0 4h20v2H2v-2zm0 4h20v2H2v-2z"/>
              </svg>
            }
            data-testid="signin-wrike-sso-button"
          >
            Entrar com Wrike
          </AuthButton>
        </div>
        )}

        {/* Forgot Password & Sign Up - Only show if email/password is enabled */}
        {isEmailPasswordEnabled && (
          <div className="space-y-3">
          <AuthButton
            type="button"
            variant="ghost"
            onClick={(e) => {
              e?.preventDefault?.()
              e?.stopPropagation?.()
              changeState('forgotPassword')
            }}
            className="w-full"
            icon={<Lock className="h-4 w-4" />}
            data-testid="signin-forgot-password-button"
          >
            Esqueci minha senha
          </AuthButton>
{/* 
          <div className="text-center" data-testid="signin-signup-section">
            <p className="text-sm text-brand-gray mb-3 font-body" data-testid="signin-signup-prompt">
              Não tem uma conta?
            </p>
            <AuthButton
              type="button"
              variant="secondary"
              onClick={(e) => {
                e?.preventDefault?.()
                e?.stopPropagation?.()
                changeState('signUp')
              }}
              className="w-full"
              icon={<UserPlus className="h-4 w-4" />}
              data-testid="signin-create-account-button"
            >
              Criar conta
            </AuthButton>
          </div> */}
          </div>
        )}
      </form>
    </AuthCard>
  )
}